/* eslint-disable no-param-reassign */
import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for smooth gradual hide/show based on scroll direction.
 * Uses RAF + direct DOM manipulation to avoid React re-render bottlenecks.
 *
 * @param {object} options - Configuration options.
 * @param {number} options.navbarHeight - The height of the navbar in pixels.
 * @param {number} options.threshold - The scroll distance in pixels to trigger a full hide/show.
 * @param {string} options.listenElementSelector - CSS selector for the element to listen to scroll events on.
 * @param {React.RefObject} options.targetRef - Ref to the element to transform.
 * @returns {object} Object containing resetVisibility function.
 */
const useGradualHide = ({
  navbarHeight = 60,
  threshold = 150,
  listenElementSelector = null,
  targetRef,
  enabled = true,
}) => {
  // Use refs to avoid stale closures and unnecessary re-renders
  const lastScrollY = useRef(0);
  const gestureStartScrollY = useRef(0);
  const activeGesture = useRef(null);
  const currentTranslateY = useRef(0);
  const targetTranslateY = useRef(0); // Target position for smooth interpolation
  const rafId = useRef(null);
  const animationRafId = useRef(null); // RAF for continuous animation loop
  const ticking = useRef(false);
  const scrollStopTimeoutId = useRef(null);
  const gestureStartPosition = useRef(0); // Track where gesture started (0 or -navbarHeight)
  const isTouching = useRef(false); // Track if finger is on screen
  const isAnimating = useRef(false); // Track if animation loop is running

  /**
   * Linear interpolation for smooth animation
   * @param {number} start - Starting value
   * @param {number} end - Target value
   * @param {number} factor - Interpolation factor (0-1), higher = faster
   */
  const lerp = (start, end, factor) => start + (end - start) * factor;

  /**
   * Apply transform directly to DOM (no React re-render)
   * @param {number} translateY - The Y translation value
   * @param {boolean} useTransition - Whether to use CSS transition for smooth animation
   */
  const applyTransform = useCallback(
    (translateY, useTransition = false) => {
      if (targetRef?.current) {
        currentTranslateY.current = translateY;
        // Temporarily enable transition for smooth snapping
        if (useTransition) {
          // eslint-disable-next-line no-param-reassign
          targetRef.current.style.transition = 'transform 0.25s cubic-bezier(0.4, 0.0, 0.2, 1)';
        }
        // Use translate3d for GPU acceleration
        // eslint-disable-next-line no-param-reassign
        targetRef.current.style.transform = `translate3d(0, ${translateY}px, 0)`;

        // Remove transition after animation completes
        if (useTransition) {
          setTimeout(() => {
            if (targetRef?.current) {
              // eslint-disable-next-line no-param-reassign
              targetRef.current.style.transition = 'none';
            }
          }, 250);
        }
      }
    },
    [targetRef],
  );

  /**
   * Continuous animation loop for smooth interpolation
   * Runs independently from scroll events
   */
  const animate = useCallback(
    () => {
      if (!targetRef?.current) {
        return;
      }

      const { current } = currentTranslateY;
      const target = targetTranslateY.current;
      const diff = Math.abs(target - current);

      // If we're close enough to target, snap to it and stop animating
      if (diff < 0.5) {
        if (diff > 0) {
          applyTransform(target, false);
        }
        isAnimating.current = false;
        return;
      }

      // Smoothly interpolate toward target
      // Use higher lerp factor (0.2) for smoother, more fluid animation
      const newPosition = lerp(current, target, 0.2);
      applyTransform(newPosition, false);

      // Continue animation loop
      animationRafId.current = requestAnimationFrame(animate);
    },
    [targetRef, applyTransform],
  );

  /**
   * Start the animation loop if not already running
   */
  const startAnimation = useCallback(
    () => {
      if (!isAnimating.current) {
        isAnimating.current = true;
        animationRafId.current = requestAnimationFrame(animate);
      }
    },
    [animate],
  );

  /**
   * Reset visibility - brings navbar back into view
   */
  const resetVisibility = useCallback(
    () => {
      // Stop continuous animation and use CSS transition for instant snap
      if (animationRafId.current) {
        cancelAnimationFrame(animationRafId.current);
        isAnimating.current = false;
      }
      targetTranslateY.current = 0;
      applyTransform(0, true);
      activeGesture.current = null;
      gestureStartPosition.current = 0;
      if (typeof window !== 'undefined') {
        lastScrollY.current = window.scrollY;
      }
    },
    [applyTransform],
  );

  /**
   * Handle scroll stop - snap back to starting position if threshold not reached
   * Only triggers when finger is lifted from screen
   */
  const handleScrollStop = useCallback(
    () => {
      // Don't snap back if finger is still on screen
      if (isTouching.current) {
        return;
      }

      if (activeGesture.current) {
        // User stopped scrolling mid-gesture without reaching threshold
        // Stop continuous animation and use CSS transition for snap back
        if (animationRafId.current) {
          cancelAnimationFrame(animationRafId.current);
          isAnimating.current = false;
        }
        targetTranslateY.current = gestureStartPosition.current;
        applyTransform(gestureStartPosition.current, true);
        activeGesture.current = null;
      }
    },
    [applyTransform],
  );

  /**
   * Process scroll updates in requestAnimationFrame
   */
  const updatePosition = useCallback(
    currentScrollY => {
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      const currentTranslate = currentTranslateY.current;

      // Reset at top of page
      if (currentScrollY <= 0) {
        applyTransform(0);
        activeGesture.current = null;
        lastScrollY.current = 0;
        ticking.current = false;
        return;
      }

      // Start hiding gesture
      if (!activeGesture.current) {
        if (scrollDirection === 'down' && currentTranslate === 0) {
          activeGesture.current = 'hiding';
          gestureStartScrollY.current = currentScrollY;
          gestureStartPosition.current = 0; // Starting from visible
        } else if (scrollDirection === 'up' && currentTranslate === -navbarHeight) {
          activeGesture.current = 'showing';
          gestureStartScrollY.current = currentScrollY;
          gestureStartPosition.current = -navbarHeight; // Starting from hidden
        }
      }

      // Handle hiding
      if (activeGesture.current === 'hiding') {
        if (scrollDirection === 'up') {
          // User changed direction - show immediately with transition
          // Stop animation loop and use CSS transition
          if (animationRafId.current) {
            cancelAnimationFrame(animationRafId.current);
            isAnimating.current = false;
          }
          targetTranslateY.current = 0;
          applyTransform(0, true);
          activeGesture.current = null;
        } else {
          const progress = currentScrollY - gestureStartScrollY.current;
          if (progress >= threshold) {
            // Threshold met - snap to hidden with smooth transition
            // Stop animation loop and use CSS transition
            if (animationRafId.current) {
              cancelAnimationFrame(animationRafId.current);
              isAnimating.current = false;
            }
            targetTranslateY.current = -navbarHeight;
            applyTransform(-navbarHeight, true);
            activeGesture.current = null;
          } else {
            // Gradual hide - update target and let animation loop handle smoothing
            const rawTranslate = -progress;
            const newTargetTranslate = Math.max(-navbarHeight, Math.min(0, rawTranslate));
            targetTranslateY.current = newTargetTranslate;

            // Start continuous animation loop for smooth interpolation
            startAnimation();
          }
        }
      }

      // Handle showing
      if (activeGesture.current === 'showing') {
        if (scrollDirection === 'down') {
          // User changed direction - hide immediately with transition
          // Stop animation loop and use CSS transition
          if (animationRafId.current) {
            cancelAnimationFrame(animationRafId.current);
            isAnimating.current = false;
          }
          targetTranslateY.current = -navbarHeight;
          applyTransform(-navbarHeight, true);
          activeGesture.current = null;
        } else {
          const progress = gestureStartScrollY.current - currentScrollY;
          if (progress >= threshold) {
            // Threshold met - snap to visible with smooth transition
            // Stop animation loop and use CSS transition
            if (animationRafId.current) {
              cancelAnimationFrame(animationRafId.current);
              isAnimating.current = false;
            }
            targetTranslateY.current = 0;
            applyTransform(0, true);
            activeGesture.current = null;
          } else {
            // Gradual show - update target and let animation loop handle smoothing
            const rawTranslate = -navbarHeight + progress;
            const newTargetTranslate = Math.max(-navbarHeight, Math.min(0, rawTranslate));
            targetTranslateY.current = newTargetTranslate;

            // Start continuous animation loop for smooth interpolation
            startAnimation();
          }
        }
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    },
    [navbarHeight, threshold, applyTransform, startAnimation],
  );

  /**
   * Scroll event handler - schedules RAF update
   */
  const handleScroll = useCallback(
    event => {
      let currentScrollY = 0;

      if (listenElementSelector) {
        const targetElement = document.querySelector(listenElementSelector);
        if (targetElement && event.target === targetElement) {
          currentScrollY = event.target.scrollTop;
        } else if (!targetElement) {
          currentScrollY = window.scrollY;
        } else {
          return;
        }
      } else {
        currentScrollY = window.scrollY;
      }

      // Clear any existing scroll stop timeout
      if (scrollStopTimeoutId.current) {
        clearTimeout(scrollStopTimeoutId.current);
      }

      // Set new scroll stop timeout (fires when user stops scrolling)
      scrollStopTimeoutId.current = setTimeout(() => {
        handleScrollStop();
      }, 150); // 150ms after last scroll event

      // Only schedule update if one isn't already pending
      if (!ticking.current) {
        rafId.current = requestAnimationFrame(() => {
          updatePosition(currentScrollY);
        });
        ticking.current = true;
      }
    },
    [listenElementSelector, updatePosition, handleScrollStop],
  );

  useEffect(
    () => {
      if (!enabled) {
        return;
      }

      // Initialize target element with will-change for GPU optimization
      if (targetRef?.current) {
        targetRef.current.style.willChange = 'transform';
        targetRef.current.style.transition = 'none'; // Remove any transitions
      }

      let scrollElement = window;
      if (listenElementSelector) {
        const targetElement = document.querySelector(listenElementSelector);
        if (targetElement) {
          scrollElement = targetElement;
        }
      }

      // Touch event handlers to track finger on/off screen
      const handleTouchStart = () => {
        isTouching.current = true;
      };

      const handleTouchEnd = () => {
        isTouching.current = false;
        // Trigger scroll stop check shortly after finger lift
        if (scrollStopTimeoutId.current) {
          clearTimeout(scrollStopTimeoutId.current);
        }
        scrollStopTimeoutId.current = setTimeout(() => {
          handleScrollStop();
        }, 100);
      };

      const handleTouchCancel = () => {
        isTouching.current = false;
      };

      scrollElement.addEventListener('scroll', handleScroll, { passive: true });
      scrollElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      scrollElement.addEventListener('touchend', handleTouchEnd, { passive: true });
      scrollElement.addEventListener('touchcancel', handleTouchCancel, { passive: true });

      // eslint-disable-next-line consistent-return
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        scrollElement.removeEventListener('touchstart', handleTouchStart);
        scrollElement.removeEventListener('touchend', handleTouchEnd);
        scrollElement.removeEventListener('touchcancel', handleTouchCancel);

        // Cancel any pending animation frames
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }
        if (animationRafId.current) {
          cancelAnimationFrame(animationRafId.current);
        }

        // Clear scroll stop timeout
        if (scrollStopTimeoutId.current) {
          clearTimeout(scrollStopTimeoutId.current);
        }

        // Clean up will-change
        if (targetRef?.current) {
          targetRef.current.style.willChange = 'auto';
        }
      };
    },
    [listenElementSelector, handleScroll, handleScrollStop, targetRef, enabled],
  );

  return { resetVisibility };
};

export default useGradualHide;
